/**
 * Sanitises restaurant-authored receipt header/footer HTML.
 *
 * The value is rendered on the storefront origin (public receipt page) via
 * `dangerouslySetInnerHTML` and inside customer emails, so anything executable must be removed
 * before it leaves the API. No HTML parser dependency exists in the repo, so this is a small
 * quote-aware tokenizer: executable elements are dropped with their content, and every other tag
 * is rebuilt from its parsed attributes so odd separators (`/`, missing whitespace, `>` inside a
 * quoted value) cannot smuggle an event handler past a regex.
 *
 * Fail-closed by construction: every `<` is either consumed as a tag we rebuild canonically
 * (validated name, validated attribute names, escaped values), dropped, or emitted as `&lt;`.
 * Where this tokenizer and a browser disagree on a tag boundary, the tokenizer always consumes
 * at least as much as the browser would, so the browser can never see markup we did not inspect.
 *
 * Ordinary formatting from the dashboard Quill editor (p, br, strong, em, u, s, a, img, hr, ul, ol,
 * li, h1-h6, span with class/style) is preserved.
 */
/** Dropped together with everything up to their closing tag. */
const RAW_TEXT_ELEMENTS = new Set([
    'script',
    'style',
    'iframe',
    'object',
    'noscript',
    'noembed',
    'noframes',
    'textarea',
    'title',
    'xmp',
]);
/** Dropped as a tag (open and close); their children are kept as plain content. */
const FORBIDDEN_ELEMENTS = new Set([
    ...RAW_TEXT_ELEMENTS,
    'embed',
    'applet',
    'frame',
    'frameset',
    'meta',
    'link',
    'base',
    'form',
    'input',
    'button',
    'select',
    'option',
    'svg',
    'math',
    'template',
    'slot',
    'plaintext',
]);
const URL_ATTRIBUTES = new Set(['href', 'src', 'action', 'formaction', 'poster', 'background', 'xlink:href', 'srcset', 'data']);
/**
 * URL attributes are allow-listed by scheme rather than deny-listed: anything with a scheme that is
 * not one of these (javascript:, vbscript:, data:, custom protocol handlers, ...) is dropped.
 * Scheme-less values (relative paths, `#anchor`, `//host/path`) are kept.
 */
const ALLOWED_URL_SCHEMES = new Set(['http', 'https', 'mailto', 'tel', 'sms']);
const URL_SCHEME_RE = /^([a-z][a-z0-9+.-]*):/i;
/** Backslash is included: CSS escapes (`\75 rl(`) can spell any of the other tokens. */
const DANGEROUS_CSS_RE = /expression\s*\(|javascript\s*:|vbscript\s*:|behaviou?r\s*:|-moz-binding|@import|url\s*\(|\\/i;
const decodeEntitiesForCheck = (value) => value
    .replace(/&#x([0-9a-f]+);?/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);?/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
    .replace(/&(?:tab|newline|colon);/gi, m => (m.toLowerCase() === '&colon;' ? ':' : ''))
    .replace(/[\u0000-\u001f\u007f]/g, '');
const isWhitespace = (c) => c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === '\f';
/** True when `url` is scheme-less or uses an allow-listed scheme (checked after entity decoding). */
const isAllowedUrl = (url) => {
    const m = URL_SCHEME_RE.exec(decodeEntitiesForCheck(url).trim());
    return !m || ALLOWED_URL_SCHEMES.has(m[1].toLowerCase());
};
/** `srcset` is a comma separated list of `<url> [descriptor]` candidates; every URL must pass. */
const isAllowedUrlAttribute = (name, value) => {
    if (name === 'srcset') {
        return value
            .split(',')
            .map(candidate => candidate.trim().split(/\s+/)[0])
            .filter(Boolean)
            .every(isAllowedUrl);
    }
    return isAllowedUrl(value);
};
/**
 * Finds the end of the tag that starts at `start` (`html[start] === '<'`), honouring quotes.
 * Returns the index of the closing `>` or -1 when the tag is unterminated.
 */
const findTagEnd = (html, start) => {
    let quote = null;
    for (let i = start + 1; i < html.length; i++) {
        const c = html[i];
        if (quote) {
            if (c === quote)
                quote = null;
            continue;
        }
        if (c === '"' || c === "'") {
            quote = c;
            continue;
        }
        if (c === '>')
            return i;
    }
    return -1;
};
/** Parses the inside of a tag (`<name ...>`), treating `/` and whitespace as separators. */
const parseAttributes = (body) => {
    const attrs = [];
    let i = 0;
    const n = body.length;
    while (i < n) {
        while (i < n && (isWhitespace(body[i]) || body[i] === '/'))
            i++;
        if (i >= n)
            break;
        let name = '';
        while (i < n && !isWhitespace(body[i]) && body[i] !== '/' && body[i] !== '=' && body[i] !== '>' && body[i] !== '"' && body[i] !== "'") {
            name += body[i++];
        }
        if (!name) {
            // Stray quote or `=`: skip it so we make progress.
            i++;
            continue;
        }
        while (i < n && isWhitespace(body[i]))
            i++;
        let value = null;
        if (body[i] === '=') {
            i++;
            while (i < n && isWhitespace(body[i]))
                i++;
            const q = body[i];
            if (q === '"' || q === "'") {
                i++;
                const close = body.indexOf(q, i);
                value = close === -1 ? body.slice(i) : body.slice(i, close);
                i = close === -1 ? n : close + 1;
            }
            else {
                let v = '';
                while (i < n && !isWhitespace(body[i]) && body[i] !== '>')
                    v += body[i++];
                value = v;
            }
        }
        attrs.push({ name: name.toLowerCase(), value });
    }
    return attrs;
};
const attributeIsSafe = (attr, reasons) => {
    const { name, value } = attr;
    if (name.startsWith('on')) {
        reasons.push(`event handler attribute "${name}"`);
        return false;
    }
    if (!/^[a-z][a-z0-9:_-]*$/.test(name)) {
        reasons.push(`malformed attribute "${name}"`);
        return false;
    }
    if (value === null) {
        return true;
    }
    if (URL_ATTRIBUTES.has(name) && !isAllowedUrlAttribute(name, value)) {
        reasons.push(`"${name}" must be a relative, http(s), mailto, tel or sms URL`);
        return false;
    }
    if (name === 'style' && DANGEROUS_CSS_RE.test(decodeEntitiesForCheck(value))) {
        reasons.push('style attribute contains executable CSS');
        return false;
    }
    return true;
};
const escapeAttributeValue = (value) => value.replace(/&(?!(?:#\d+|#x[0-9a-f]+|[a-z]+);)/gi, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const rebuildTag = (name, attrs, selfClosing) => {
    const parts = attrs.map(a => (a.value === null ? a.name : `${a.name}="${escapeAttributeValue(a.value)}"`));
    return `<${name}${parts.length ? ' ' + parts.join(' ') : ''}${selfClosing ? ' /' : ''}>`;
};
const sanitizeCore = (input) => {
    const reasons = [];
    let out = '';
    let i = 0;
    const n = input.length;
    while (i < n) {
        const lt = input.indexOf('<', i);
        if (lt === -1) {
            out += input.slice(i);
            break;
        }
        out += input.slice(i, lt);
        // Comments: drop entirely (conditional comments can carry script in legacy engines).
        if (input.startsWith('<!--', lt)) {
            reasons.push('HTML comments are not allowed');
            const end = input.indexOf('-->', lt + 4);
            i = end === -1 ? n : end + 3;
            continue;
        }
        // Doctype / processing instruction / CDATA: drop the tag.
        if (input[lt + 1] === '!' || input[lt + 1] === '?') {
            reasons.push('declarations are not allowed');
            const end = findTagEnd(input, lt);
            i = end === -1 ? n : end + 1;
            continue;
        }
        const nameMatch = /^<\/?([a-zA-Z][a-zA-Z0-9-]*)/.exec(input.slice(lt, lt + 64));
        if (!nameMatch) {
            // Not a tag (`<3`, `a < b`): keep as literal text, escaped so it cannot become one later.
            out += '&lt;';
            i = lt + 1;
            continue;
        }
        const end = findTagEnd(input, lt);
        if (end === -1) {
            // Unterminated tag: nothing after this point can render safely, drop the remainder.
            reasons.push('unterminated tag');
            break;
        }
        const isClosing = input[lt + 1] === '/';
        const name = nameMatch[1].toLowerCase();
        const rawTag = input.slice(lt, end + 1);
        if (FORBIDDEN_ELEMENTS.has(name)) {
            reasons.push(`<${name}> is not allowed`);
            if (!isClosing && RAW_TEXT_ELEMENTS.has(name)) {
                const closeRe = new RegExp(`</\\s*${name}\\s*>`, 'ig');
                closeRe.lastIndex = end + 1;
                const m = closeRe.exec(input);
                i = m ? m.index + m[0].length : n;
            }
            else {
                i = end + 1;
            }
            continue;
        }
        if (isClosing) {
            out += `</${name}>`;
            i = end + 1;
            continue;
        }
        const selfClosing = /\/\s*>$/.test(rawTag);
        const body = rawTag.slice(1 + name.length, selfClosing ? rawTag.lastIndexOf('/') : rawTag.length - 1);
        const attrs = parseAttributes(body).filter(a => attributeIsSafe(a, reasons));
        out += rebuildTag(name, attrs, selfClosing);
        i = end + 1;
    }
    return { html: out, reasons };
};
export const sanitizeReceiptRichText = (input) => {
    if (!input || typeof input !== 'string') {
        return undefined;
    }
    const trimmed = sanitizeCore(input).html.trim();
    return trimmed ? trimmed : undefined;
};
/** Save-time check: rejects any value the sanitiser would have to alter. */
export const validateReceiptRichText = (input) => {
    if (!input || typeof input !== 'string') {
        return { ok: true };
    }
    const { reasons } = sanitizeCore(input);
    if (reasons.length === 0) {
        return { ok: true };
    }
    const unique = Array.from(new Set(reasons));
    return { ok: false, error: unique.slice(0, 3).join('; ') };
};
//# sourceMappingURL=receipt-rich-text.js.map